import { useState } from "react";

/* ─────────────────────────────────────────────────────
   DESIGN TOKENS & GLOBAL CSS (injected once)
───────────────────────────────────────────────────── */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth;font-size:16px}
body{font-family:'Roboto',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;-webkit-font-smoothing:antialiased;color:#1a202c;line-height:1.6;overflow-x:hidden}
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:#f0eeff}
::-webkit-scrollbar-thumb{background:#9C27B0;border-radius:99px}
a{text-decoration:none;color:inherit}
button{font-family:inherit}
:root{
  --p50:#F3E5F5;--p100:#E1BEE7;--p200:#CE93D8;--p300:#BA68C8;--p400:#9C27B0;
  --p500:#7B1FA2;--p600:#6A1B9A;--p700:#4A148C;--p800:#38006B;--p900:#2A004F;
  --s50:#FCE4EC;--s100:#F8BBD0;--s300:#F06292;--s400:#EC407A;--s500:#E91E63;--s600:#D81B60;
  --white:#FFFFFF;--black:#000000;
  --g50:#f9fafb;--g100:#f7fafc;--g200:#edf2f7;--g300:#e2e8f0;--g400:#cbd5e0;
  --g500:#a0aec0;--g600:#718096;--g700:#4a5568;--g800:#2d3748;--g900:#1a202c;
  --ok100:#dcfce7;--ok500:#16a34a;
  --err100:#fee2e2;--err500:#dc2626;
  --warn100:#fef3c7;--warn500:#f59e0b;
  --info100:#EDE7F6;--info500:#5E35B1;
  --border:#E6E0F0;--bg:#F4F6F9;--surface:#FBF7FF;--dark:#1A0B2E;
  --ch:#1A1A1A;--cb:#555555;--cc:#777777;
  --ff:'Roboto',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  --r-xs:4px;--r-sm:6px;--r-md:10px;--r-lg:16px;--r-xl:24px;--r-full:9999px;
  --sh-xs:0 1px 3px rgba(0,0,0,0.06);--sh-sm:0 2px 6px rgba(0,0,0,0.08);
  --sh-md:0 4px 12px rgba(0,0,0,0.12);--sh-lg:0 8px 24px rgba(0,0,0,0.14);
  --tr-f:all 0.15s ease;--tr-b:all 0.3s ease;
}
/* Global utility classes reused across screens */
.page-wrap{padding:22px 24px;background:var(--bg);min-height:100vh;font-family:var(--ff)}
.page-head{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:18px;gap:12px;flex-wrap:wrap}
.page-title{font-size:22px;font-weight:700;color:var(--ch);margin:0 0 3px}
.page-sub{font-size:12px;color:var(--cc);margin:0}
.head-actions{display:flex;gap:10px;align-items:center;flex-shrink:0}
.card{background:#fff;border:1px solid var(--border);border-radius:var(--r-lg);padding:16px;margin-bottom:14px}
.card-sm{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:12px 14px}
.sec-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}
.sec-title{font-size:14px;font-weight:700;color:var(--ch);margin:0;display:flex;align-items:center;gap:6px}
.sec-link{font-size:12px;font-weight:600;color:var(--p700);background:none;border:none;cursor:pointer;padding:0}
.sec-link:hover{color:var(--p800);text-decoration:underline}
.btn-primary{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:var(--p700);color:#fff;border:none;border-radius:var(--r-md);font-size:13px;font-weight:600;cursor:pointer;transition:var(--tr-f);white-space:nowrap}
.btn-primary:hover{background:var(--p800);transform:translateY(-1px)}
.btn-outline{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#fff;color:var(--p700);border:1.5px solid var(--p300);border-radius:var(--r-md);font-size:13px;font-weight:600;cursor:pointer;transition:var(--tr-f);white-space:nowrap}
.btn-outline:hover{background:var(--p50)}
.btn-ghost{display:inline-flex;align-items:center;gap:4px;padding:5px 10px;background:transparent;color:var(--p700);border:none;border-radius:var(--r-sm);font-size:12px;font-weight:600;cursor:pointer;transition:var(--tr-f)}
.btn-ghost:hover{background:var(--p50)}
.badge-red{display:inline-block;padding:2px 8px;border-radius:var(--r-full);font-size:10px;font-weight:600;background:var(--err100);color:var(--err500)}
.badge-orange{display:inline-block;padding:2px 8px;border-radius:var(--r-full);font-size:10px;font-weight:600;background:var(--warn100);color:var(--warn500)}
.badge-green{display:inline-block;padding:2px 8px;border-radius:var(--r-full);font-size:10px;font-weight:600;background:var(--ok100);color:var(--ok500)}
.badge-purple{display:inline-block;padding:2px 8px;border-radius:var(--r-full);font-size:10px;font-weight:600;background:var(--p100);color:var(--p700)}
.divider{height:1px;background:var(--border);margin:10px 0}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:flex-start}
.three-col{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;align-items:flex-start}
.left-col{display:flex;flex-direction:column;gap:14px;min-width:0}
.right-col{display:flex;flex-direction:column;gap:14px}
.risk-bar{height:4px;border-radius:2px;background:var(--g200);margin-top:4px;overflow:hidden}
.risk-fill-red{height:100%;border-radius:2px;background:var(--err500)}
.risk-fill-orange{height:100%;border-radius:2px;background:var(--warn500)}
.risk-fill-green{height:100%;border-radius:2px;background:var(--ok500)}
.tab-bar{display:flex;gap:0;border-bottom:2px solid var(--border);margin-bottom:14px;overflow-x:auto}
.tab{padding:7px 14px;font-size:12px;font-weight:500;color:var(--cc);cursor:pointer;border:none;border-bottom:2px solid transparent;margin-bottom:-2px;white-space:nowrap;background:none;transition:var(--tr-f);font-family:var(--ff)}
.tab:hover{color:var(--p700)}
.tab.active{color:var(--p700);border-bottom:2px solid var(--p700);font-weight:700;margin-bottom:-2px}
.life-chip{display:inline-flex;align-items:center;gap:6px;background:var(--p100);border:1px solid var(--p300);border-radius:var(--r-full);padding:3px 10px 3px 7px;font-size:11px;font-weight:600;color:var(--p700)}
.icon-circle{width:34px;height:34px;border-radius:50%;background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0}
.icon-sq{width:30px;height:30px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}
/* Patient card */
.pat-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-lg);padding:16px 18px;display:flex;align-items:flex-start;gap:16px;margin-bottom:16px;flex-wrap:wrap}
.pat-avatar{width:68px;height:68px;border-radius:50%;flex-shrink:0;background:var(--p100);display:flex;align-items:center;justify-content:center;overflow:hidden}
.pat-info{flex:1;min-width:160px}
.pat-name{font-size:17px;font-weight:700;color:var(--ch);margin:0 0 3px;display:flex;align-items:center;gap:6px}
.verified{width:17px;height:17px;background:var(--ok500);border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:9px;color:#fff;flex-shrink:0}
.pat-meta{font-size:11px;color:var(--cc);margin:0 0 6px}
.contact-row{font-size:11px;color:var(--cb);display:flex;align-items:center;gap:5px;padding:2px 0}
.pat-col{display:flex;flex-direction:column;gap:3px;min-width:150px}
.col-lbl{font-size:10px;color:var(--cc);margin:0 0 2px}
.col-val{font-size:12px;font-weight:600;color:var(--ch);margin:0}
.col-val-pink{font-size:12px;font-weight:700;color:var(--s500);margin:0}
.view-prof-btn{display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#fff;color:var(--p700);border:1px solid var(--p300);border-radius:var(--r-sm);font-size:11px;font-weight:600;cursor:pointer;margin-top:7px;transition:var(--tr-f)}
.view-prof-btn:hover{background:var(--p100)}
/* Timeline */
.timeline{display:flex;align-items:flex-start;gap:0;overflow-x:auto;padding:6px 0 4px}
.tl-item{display:flex;flex-direction:column;align-items:center;flex:1;min-width:80px;position:relative}
.tl-dot{width:32px;height:32px;border-radius:50%;background:var(--p100);border:2px solid var(--p300);display:flex;align-items:center;justify-content:center;font-size:13px;position:relative;z-index:1;flex-shrink:0}
.tl-dot.active{background:var(--p700);border-color:var(--p700);color:#fff;box-shadow:0 0 0 4px var(--p100)}
.tl-dot.done{background:var(--p100);border-color:var(--p400)}
.tl-dot.muted{background:var(--g100);border:2px dashed var(--g300);color:var(--cc)}
.tl-line{position:absolute;top:16px;left:calc(50% + 16px);right:-50%;height:2px;background:var(--p200)}
.tl-line.muted-line{background:var(--g300)}
.tl-lbl{font-size:10px;font-weight:600;color:var(--ch);text-align:center;margin-top:5px}
.tl-lbl.active{color:var(--p700)}
.tl-lbl.muted{color:var(--cc)}
.tl-sub{font-size:9px;color:var(--cc);text-align:center;margin-top:1px}
/* Care plan */
.cp-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
.cp-box{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:12px;display:flex;flex-direction:column;gap:7px}
.cp-title{font-size:12px;font-weight:700;color:var(--ch);display:flex;align-items:center;gap:5px;margin:0}
.cp-item{font-size:11px;color:var(--cb);display:flex;align-items:flex-start;gap:5px}
.check{color:var(--ok500);font-size:11px;flex-shrink:0;margin-top:1px}
/* Responsive */
@media(max-width:1100px){
  .cp-grid{grid-template-columns:1fr 1fr}
}
@media(max-width:768px){
  .page-wrap{padding:14px}
  .page-head{flex-direction:column}
  .two-col,.three-col{grid-template-columns:1fr}
  .pat-card{gap:12px}
  .cp-grid{grid-template-columns:1fr 1fr}
  .whr-sidebar{position:fixed!important;top:0;bottom:0;z-index:200;transition:left 0.25s ease}
}
@media(max-width:480px){
  .cp-grid{grid-template-columns:1fr}
}
`;

/* ─────────────────────────────────────────────────────
   SHARED COMPONENTS
───────────────────────────────────────────────────── */
function PatientHeader({ lifeStage = "Reproductive Age", activeJourney = "Preconception / Fertility Readiness", summaryLabel = "Record Summary", summaryFields = [] }) {
  return (
    <div className="pat-card">
      <div className="pat-avatar">
        <svg viewBox="0 0 68 68" width="68" height="68">
          <circle cx="34" cy="34" r="34" fill="#EDE9FE"/>
          <ellipse cx="34" cy="28" rx="11" ry="12" fill="#9C27B0"/>
          <ellipse cx="34" cy="58" rx="19" ry="13" fill="#9C27B0"/>
        </svg>
      </div>
      <div className="pat-info">
        <p className="pat-name">Priya Mehta <span className="verified">✓</span></p>
        <p className="pat-meta">34 Years • 12 Mar 1991 • Female</p>
        <div className="contact-row">📞 +91 98765 43210</div>
        <div className="contact-row">✉️ priya.mehta@gmail.com</div>
        <div className="contact-row">📍 Mumbai, Maharashtra <span style={{marginLeft:8,fontSize:10,color:"var(--p700)",fontWeight:600}}>🩸 O+</span></div>
        <button className="view-prof-btn">View Profile →</button>
      </div>
      <div className="pat-col">
        <p className="col-lbl">Life Stage</p>
        <div style={{display:"flex",alignItems:"center",gap:6,margin:"3px 0 8px"}}>
          <span style={{fontSize:18}}>🌸</span>
          <span className="life-chip">{lifeStage}</span>
        </div>
        <p className="col-lbl">Active Journey</p>
        <p className="col-val-pink">{activeJourney}</p>
      </div>
      {summaryFields.length > 0 && (
        <div className="pat-col">
          <p className="col-lbl" style={{fontWeight:700,marginBottom:6}}>{summaryLabel}</p>
          {summaryFields.map((f,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:11,padding:"2px 0",borderBottom:"1px solid var(--border)"}}>
              <span style={{color:"var(--cc)"}}>{f.label}</span>
              <span style={{color:"var(--ch)",fontWeight:600,textAlign:"right"}}>{f.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HealthTimeline({ events }) {
  const defaultEvs = [
    { icon:"👧", label:"Menarche", sub:"Age 13", done:true },
    { icon:"📋", label:"Irregular Periods", sub:"Age 19", done:true },
    { icon:"⚕️", label:"PCOS Diagnosis", sub:"2020", done:true },
    { icon:"💊", label:"Started Contraception", sub:"2021", done:true },
    { icon:"🚫", label:"Stopped Contraception", sub:"Jan 2024", done:true },
    { icon:"🌸", label:"Preconception Journey", sub:"Mar 2024", active:true },
    { icon:"🤰", label:"Planned Pregnancy", sub:"(Upcoming)", muted:true },
  ];
  const evs = events || defaultEvs;
  return (
    <div className="timeline">
      {evs.map((ev,i) => (
        <div key={i} className="tl-item">
          {i < evs.length - 1 && <div className={`tl-line${ev.muted?" muted-line":""}`} />}
          <div className={`tl-dot${ev.active?" active":ev.done?" done":ev.muted?" muted":""}`}>{ev.icon}</div>
          <p className={`tl-lbl${ev.active?" active":ev.muted?" muted":""}`}>{ev.label}</p>
          <p className="tl-sub">{ev.sub}</p>
        </div>
      ))}
    </div>
  );
}

function CarePlanSection({ goals, programs, tasks, followup }) {
  const g = goals || ["Achieve healthy pregnancy in next 6 months","Improve menstrual regularity","Optimize weight and PCOS symptoms"];
  const p = programs || [{name:"Preconception Care Program",status:"In Progress"},{name:"PCOS Management Program",status:"In Progress"}];
  const t = tasks || [{name:"Complete TSH Test",due:"Due on 18 May 2026"},{name:"Submit Day 2 Hormone Panel",due:"Due on 22 May 2026"},{name:"Track ovulation this cycle",due:"Due on 25 May 2026"}];
  const fu = followup || {date:"01 Jun 2026, 10:30 AM",doctor:"Dr. Ananya Sharma",specialty:"Gynecologist",clinic:"Avexa Women's Health Clinic, Mumbai"};
  return (
    <div className="cp-grid">
      <div className="cp-box">
        <p className="cp-title">🎯 Goals</p>
        {g.map((x,i) => <div key={i} className="cp-item"><span className="check">✓</span><span>{x}</span></div>)}
        <button className="btn-ghost" style={{marginTop:4,fontSize:11}}>View Goals →</button>
      </div>
      <div className="cp-box">
        <p className="cp-title">💜 Active Programs</p>
        {p.map((x,i) => (
          <div key={i} className="cp-item" style={{justifyContent:"space-between"}}>
            <span style={{flex:1}}>{x.name}</span>
            <span className="badge-green" style={{flexShrink:0}}>{x.status}</span>
          </div>
        ))}
        <button className="btn-ghost" style={{marginTop:4,fontSize:11}}>View Programs →</button>
      </div>
      <div className="cp-box">
        <p className="cp-title">📋 Upcoming Tasks</p>
        {t.map((x,i) => (
          <div key={i} className="cp-item">
            <span style={{color:"var(--p400)",fontSize:11}}>☐</span>
            <div><div style={{fontWeight:500,fontSize:11,color:"var(--ch)"}}>{x.name}</div><div style={{fontSize:10,color:"var(--cc)"}}>{x.due}</div></div>
          </div>
        ))}
        <button className="btn-ghost" style={{marginTop:4,fontSize:11}}>View All Tasks →</button>
      </div>
      <div className="cp-box">
        <p className="cp-title">📅 Next Follow-up</p>
        <div style={{fontSize:13,fontWeight:700,color:"var(--s500)",margin:"3px 0"}}>{fu.date}</div>
        <div style={{fontSize:12,fontWeight:600,color:"var(--ch)"}}>{fu.doctor}</div>
        <div style={{fontSize:11,color:"var(--cc)"}}>{fu.specialty}</div>
        <div style={{fontSize:10,color:"var(--cc)",marginTop:2}}>📍 {fu.clinic}</div>
        <button className="btn-ghost" style={{marginTop:6,fontSize:11,color:"var(--s500)"}}>Reschedule</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   OVERVIEW SCREEN
───────────────────────────────────────────────────── */
const overviewCSS = `
.ov-snap-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.ov-snap-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:13px}
.ov-snap-lbl{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin:0 0 5px}
.ov-snap-val{font-size:15px;font-weight:700;margin:0 0 2px}
.ov-snap-sub{font-size:10px;color:var(--cc)}
.risk-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:9px}
.risk-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px;display:flex;flex-direction:column;gap:3px}
.risk-name{font-size:11px;color:var(--ch);font-weight:600;margin:0 0 2px}
.risk-score-txt{font-size:10px;color:var(--cc)}
.ai-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:11px}
.ai-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:13px;display:flex;flex-direction:column;gap:5px;transition:var(--tr-f)}
.ai-card:hover{border-color:var(--p300);box-shadow:var(--sh-sm)}
.ai-icon{width:38px;height:38px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:17px;margin-bottom:3px}
.ai-title{font-size:12px;font-weight:700;color:var(--p700);margin:0 0 2px;line-height:1.3}
.ai-desc{font-size:10px;color:var(--cb);margin:0;line-height:1.4;flex:1}
.ai-btn{display:inline-flex;align-items:center;justify-content:center;padding:4px 10px;background:var(--p700);color:#fff;border:none;border-radius:var(--r-sm);font-size:10px;font-weight:600;cursor:pointer;transition:var(--tr-f);margin-top:3px;font-family:var(--ff)}
.ai-btn:hover{background:var(--p800)}
.ov-tab-content{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px}
.ov-col-title{font-size:12px;font-weight:700;color:var(--ch);margin:0 0 7px}
.lab-row{display:flex;align-items:center;justify-content:space-between;padding:5px 0;border-bottom:1px solid var(--g100);gap:8px;font-size:11px}
.lab-row:last-child{border-bottom:none}
.due-row{display:flex;align-items:center;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--g100);font-size:11px}
.due-row:last-child{border-bottom:none}
.appt-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:13px;text-align:center}
.appt-day{font-size:28px;font-weight:700;color:var(--p700);line-height:1}
.appt-mon{font-size:11px;color:var(--cc);text-transform:uppercase;letter-spacing:.06em}
@media(max-width:1100px){.ov-snap-grid{grid-template-columns:repeat(3,1fr)}.risk-grid{grid-template-columns:repeat(3,1fr)}.ai-grid{grid-template-columns:repeat(3,1fr)}.ov-tab-content{grid-template-columns:1fr}}
@media(max-width:768px){.ov-snap-grid{grid-template-columns:repeat(2,1fr)}.risk-grid{grid-template-columns:repeat(2,1fr)}.ai-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:480px){.ai-grid{grid-template-columns:1fr}}
`;

function OverviewScreen() {
  const [tab, setTab] = useState("Overview");
  const TABS = ["Overview","Menstrual Health","Reproductive Health","Assessments","Labs & Reports","Medications","Lifestyle","Notes"];
  const risks = [
    {icon:"🔵",name:"PCOS Risk",badge:"Moderate Risk",cls:"badge-orange",score:58,fillCls:"risk-fill-orange"},
    {icon:"🤰",name:"Pregnancy Risk",badge:"Low Risk",cls:"badge-green",score:22,fillCls:"risk-fill-green"},
    {icon:"🦋",name:"Thyroid Risk",badge:"Low Risk",cls:"badge-green",score:18,fillCls:"risk-fill-green"},
    {icon:"🩸",name:"Anemia Risk",badge:"Low Risk",cls:"badge-green",score:24,fillCls:"risk-fill-green"},
    {icon:"🧠",name:"Mental Health Risk",badge:"Low Risk",cls:"badge-green",score:20,fillCls:"risk-fill-green"},
    {icon:"❤️",name:"Metabolic Risk",badge:"Moderate Risk",cls:"badge-orange",score:52,fillCls:"risk-fill-orange"},
  ];
  const aiActions = [
    {icon:"🧪",title:"Complete TSH Test",desc:"Thyroid screening is recommended before conception.",btn:"Order Test"},
    {icon:"🔵",title:"PCOS Management",desc:"Your symptoms indicate moderate PCOS risk. Early management helps improve fertility.",btn:"Start Program"},
    {icon:"📅",title:"Book Preconception Consultation",desc:"Discuss fertility, nutrition and lifestyle readiness.",btn:"Book Appointment"},
    {icon:"🥗",title:"Lifestyle Optimization",desc:"Personalized plan for nutrition, exercise, sleep and stress management.",btn:"View Plan"},
    {icon:"💊",title:"Folic Acid Supplementation",desc:"Continue folic acid for at least 3 months before conception.",btn:"Learn More"},
  ];
  return (
    <div className="page-wrap">
      <div className="page-head">
        <div><h1 className="page-title">Unified Women's Health Record</h1><p className="page-sub">Comprehensive • Lifelong • Personalized</p></div>
        <div className="head-actions">
          <button className="btn-outline">🔗 Share Record</button>
          <button className="btn-primary">+ Add Note</button>
        </div>
      </div>
      <PatientHeader summaryFields={[{label:"Blood Group",value:"O+"},{label:"Height / Weight",value:"162 cm / 68 kg"},{label:"BMI",value:"25.9 (Overweight)"},{label:"Last Visit",value:"12 May 2026"}]} />

      {/* Life Stage Snapshot */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Life Stage Snapshot <span style={{fontSize:12,fontWeight:400,color:"var(--cc)"}}>ⓘ</span></h2></div>
        <div className="ov-snap-grid">
          <div className="ov-snap-card">
            <p className="ov-snap-lbl" style={{color:"var(--p700)"}}>Current Life Stage</p>
            <p className="ov-snap-val" style={{color:"var(--p700)"}}>Reproductive Age</p>
            <div style={{fontSize:28,margin:"6px 0"}}>🌸</div>
            <p className="ov-snap-sub">Stage since Jan 2023</p>
          </div>
          <div className="ov-snap-card">
            <p className="ov-snap-lbl" style={{color:"var(--ok500)"}}>Active Journey</p>
            <p className="ov-snap-val" style={{color:"var(--ok500)",fontSize:14}}>Preconception</p>
            <div style={{fontSize:26,margin:"6px 0"}}>💜</div>
            <p className="ov-snap-sub">Goal: Plan pregnancy in next 6 months</p>
          </div>
          <div className="ov-snap-card">
            <p className="ov-snap-lbl" style={{color:"var(--err500)"}}>Key Risks</p>
            {[{name:"PCOS Risk",lvl:"Moderate",cls:"badge-orange"},{name:"Thyroid Risk",lvl:"Low",cls:"badge-green"},{name:"Anemia Risk",lvl:"Low",cls:"badge-green"}].map((r,i) => (
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0",borderBottom:i<2?"1px solid var(--g100)":"none",fontSize:11}}>
                <span style={{color:"var(--cb)"}}>{r.name}</span><span className={r.cls}>{r.lvl}</span>
              </div>
            ))}
            <button className="sec-link" style={{marginTop:5,fontSize:10}}>View all (5)</button>
          </div>
          <div className="ov-snap-card">
            <p className="ov-snap-lbl" style={{color:"var(--warn500)"}}>Due Actions</p>
            {["Pap Smear","TSH Test","Vitamin D Test"].map((a,i) => (
              <div key={i} className="due-row">
                <span style={{fontSize:11,color:"var(--cb)",display:"flex",alignItems:"center",gap:5}}>☐ {a}</span>
                <span className="badge-red">Due</span>
              </div>
            ))}
          </div>
          <div className="appt-card">
            <p className="ov-snap-lbl" style={{color:"var(--p700)"}}>Next Appointment</p>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",margin:"4px 0 6px"}}>
              <span className="appt-day">20</span><span className="appt-mon">May</span>
            </div>
            <div style={{fontSize:12,fontWeight:600,color:"var(--ch)"}}>Dr. Ananya Sharma</div>
            <div style={{fontSize:11,color:"var(--cc)"}}>Gynecologist</div>
            <div style={{fontSize:12,fontWeight:600,color:"var(--cb)",margin:"3px 0 7px"}}>10:30 AM</div>
            <button style={{fontSize:11,color:"var(--p700)",fontWeight:600,cursor:"pointer",background:"none",border:"none",fontFamily:"var(--ff)"}}>Reschedule</button>
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className="card">
        <div className="sec-head">
          <h2 className="sec-title">Risk Summary <span style={{fontSize:12,fontWeight:400,color:"var(--cc)"}}>ⓘ</span></h2>
          <span style={{fontSize:10,color:"var(--cc)"}}>Risk Model: Avexa Women's Health AI ✨</span>
        </div>
        <div className="risk-grid">
          {risks.map((r,i) => (
            <div key={i} className="risk-card">
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                <span style={{fontSize:16}}>{r.icon}</span><span className={r.cls}>{r.badge}</span>
              </div>
              <p className="risk-name">{r.name}</p>
              <p className="risk-score-txt">Score {r.score} /100</p>
              <div className="risk-bar"><div className={r.fillCls} style={{width:`${r.score}%`}} /></div>
            </div>
          ))}
        </div>
        <div style={{textAlign:"right",marginTop:9}}><button className="sec-link">View full risk report →</button></div>
      </div>

      {/* Timeline */}
      <div className="card">
        <div className="sec-head">
          <h2 className="sec-title">Women's Health Timeline <span style={{fontSize:12,fontWeight:400,color:"var(--cc)"}}>ⓘ</span></h2>
          <button className="btn-ghost">View Full Timeline</button>
        </div>
        <HealthTimeline />
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="tab-bar">
          {TABS.map(t => <button key={t} className={`tab${tab===t?" active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
        </div>
        {tab === "Overview" ? (
          <div className="ov-tab-content">
            <div>
              <p className="ov-col-title">Active Conditions</p>
              {["Irregular Periods","PCOS (Polycystic Ovary Syndrome)","Vitamin D Deficiency"].map((c,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:"var(--cb)",padding:"3px 0"}}>
                  <span style={{color:"var(--err500)"}}>●</span> {c}
                </div>
              ))}
              <button className="sec-link" style={{marginTop:7,fontSize:11}}>View all conditions →</button>
              <div className="divider" />
              <p className="ov-col-title">Current Medications</p>
              {[{n:"Metformin 500mg",d:"1-0-1"},{n:"Vitamin D3 60,000 IU",d:"Weekly"},{n:"Folic Acid 5mg",d:"1-0-0"}].map((m,i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"4px 0",borderBottom:"1px solid var(--g100)"}}>
                  <span style={{color:"var(--cb)"}}>{m.n}</span><span style={{color:"var(--ch)",fontWeight:600}}>{m.d}</span>
                </div>
              ))}
              <button className="sec-link" style={{marginTop:7,fontSize:11}}>View all medications →</button>
            </div>
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                <p className="ov-col-title" style={{margin:0}}>Recent Labs</p>
                <button className="sec-link" style={{fontSize:10}}>View all</button>
              </div>
              {[
                {n:"TSH",v:"2.45 mIU/L",d:"05 May 2026",s:"Normal",cls:"badge-green"},
                {n:"HbA1c",v:"5.6 %",d:"05 May 2026",s:"Normal",cls:"badge-green"},
                {n:"Vitamin D",v:"18 ng/mL",d:"05 May 2026",s:"Low",cls:"badge-red"},
                {n:"Hemoglobin",v:"11.2 g/dL",d:"05 May 2026",s:"Low",cls:"badge-red"},
              ].map((l,i) => (
                <div key={i} className="lab-row">
                  <span style={{color:"var(--ch)",fontWeight:500,flex:1}}>{l.n}</span>
                  <span style={{color:"var(--cb)"}}>{l.v}</span>
                  <span style={{color:"var(--cc)",fontSize:10}}>{l.d}</span>
                  <span className={l.cls}>{l.s}</span>
                </div>
              ))}
              <div className="divider" />
              <p className="ov-col-title">Recent Assessments</p>
              {[{n:"PHQ-9 Score",s:6,l:"Mild",cls:"badge-orange"},{n:"GAD-7 Score",s:5,l:"Mild",cls:"badge-orange"},{n:"PCOS Assessment",s:null,l:"Moderate Risk",cls:"badge-orange"}].map((a,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",borderBottom:"1px solid var(--g100)",fontSize:11}}>
                  <span style={{flex:1,color:"var(--ch)",fontWeight:500}}>{a.n}</span>
                  {a.s!==null&&<span style={{fontWeight:600,color:"var(--cb)"}}>{a.s}</span>}
                  <span className={a.cls}>{a.l}</span>
                </div>
              ))}
              <button className="sec-link" style={{marginTop:7,fontSize:11}}>View all assessments →</button>
            </div>
            <div>
              <p className="ov-col-title">Health Highlights</p>
              {["Cycles are irregular (35–45 days)","Experiencing acne and hair fall","Weight gain of 6 kg in last 1 year","No known drug allergies","Sleep quality: Average (6 hrs/night)"].map((h,i) => (
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:5,fontSize:11,color:"var(--cb)",padding:"3px 0"}}>
                  <span style={{color:"var(--ok500)"}}>✓</span> {h}
                </div>
              ))}
              <button className="sec-link" style={{marginTop:7,fontSize:11}}>View all highlights →</button>
              <div className="divider" />
              <p className="ov-col-title">Preventive Care</p>
              {[{n:"Pap Smear",s:"Due",cls:"badge-red"},{n:"HPV Vaccination",s:"Completed",cls:"badge-green"},{n:"Dental Checkup",s:"Due",cls:"badge-red"},{n:"Eye Checkup",s:"Due",cls:"badge-red"}].map((p,i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"4px 0",borderBottom:"1px solid var(--g100)"}}>
                  <span style={{color:"var(--cb)"}}>{p.n}</span><span className={p.cls}>{p.s}</span>
                </div>
              ))}
              <button className="sec-link" style={{marginTop:7,fontSize:11}}>View preventive care →</button>
            </div>
          </div>
        ) : (
          <div style={{padding:"28px",textAlign:"center",color:"var(--cc)",fontSize:13}}>
            <div style={{fontSize:28,marginBottom:8}}>📋</div>
            <p>{tab} — select to view detailed data</p>
          </div>
        )}
      </div>

      {/* AI Actions */}
      <div className="card">
        <div className="sec-head">
          <h2 className="sec-title">AI Recommended Actions <span style={{fontSize:12,fontWeight:400,color:"var(--cc)"}}>ⓘ</span></h2>
          <span style={{fontSize:10,color:"var(--cc)"}}>Powered by Avexa Women's Health AI ✨</span>
        </div>
        <div className="ai-grid">
          {aiActions.map((a,i) => (
            <div key={i} className="ai-card">
              <div className="ai-icon">{a.icon}</div>
              <p className="ai-title">{a.title}</p>
              <p className="ai-desc">{a.desc}</p>
              <button className="ai-btn">{a.btn}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Care Plan */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Care Plan <span style={{fontSize:12,fontWeight:400,color:"var(--cc)"}}>ⓘ</span></h2></div>
        <CarePlanSection />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   LIFE STAGE SCREEN
───────────────────────────────────────────────────── */
const lifeStageCSS = `
.ls-conf-row{display:grid;grid-template-columns:repeat(5,1fr);gap:11px;margin-bottom:16px}
.ls-conf-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:13px}
.ls-conf-lbl{font-size:10px;color:var(--cc);font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin:0 0 5px}
.ls-conf-val-g{font-size:17px;font-weight:700;color:var(--ok500);margin:0 0 4px}
.ls-conf-val-p{font-size:15px;font-weight:700;color:var(--p700);margin:0 0 4px}
.ls-prog-bar{height:5px;border-radius:3px;background:var(--g200);overflow:hidden;margin:4px 0}
.ls-prog-fill{height:100%;border-radius:3px;background:var(--ok500)}
.ls-conf-sub{font-size:10px;color:var(--cc);margin:3px 0 0}
.ls-tag{display:inline-block;background:var(--p100);color:var(--p700);font-size:10px;font-weight:600;border-radius:var(--r-full);padding:1px 7px;margin-top:3px}
.ls-journey{display:flex;align-items:flex-start;gap:0;overflow-x:auto;padding:8px 0 4px;margin-bottom:16px}
.ls-j-item{display:flex;flex-direction:column;align-items:center;flex:1;min-width:90px;position:relative}
.ls-j-dot{width:36px;height:36px;border-radius:50%;background:var(--g200);border:2px solid var(--g300);display:flex;align-items:center;justify-content:center;font-size:14px;position:relative;z-index:1}
.ls-j-dot.active{background:var(--p700);border-color:var(--p700);color:#fff;box-shadow:0 0 0 4px var(--p100)}
.ls-j-dot.done{background:var(--ok100);border-color:var(--ok500)}
.ls-j-dot.muted{border:2px dashed var(--g300);background:var(--g100)}
.ls-j-line{position:absolute;top:18px;left:calc(50% + 18px);right:-50%;height:2px;background:var(--g300)}
.ls-j-line.done{background:var(--p300)}
.ls-j-lbl{font-size:11px;font-weight:600;color:var(--ch);text-align:center;margin-top:7px}
.ls-j-lbl.active{color:var(--p700)}
.ls-j-lbl.muted{color:var(--cc)}
.ls-two{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
.ls-panel{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:15px}
.ls-panel-title{font-size:13px;font-weight:700;color:var(--ch);margin:0 0 10px;display:flex;align-items:center;gap:7px}
.ls-panel-icon{width:26px;height:26px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:13px}
.ls-bullet{display:flex;align-items:flex-start;gap:6px;padding:3px 0;font-size:11px;color:var(--cb);line-height:1.4}
.ls-dot{width:6px;height:6px;border-radius:50%;background:var(--p400);flex-shrink:0;margin-top:4px}
.ls-warn{display:flex;align-items:flex-start;gap:6px;padding:3px 0;font-size:11px;color:var(--cb);line-height:1.4}
.ls-hist-table{display:grid;grid-template-columns:1fr 1fr;gap:3px 18px}
.ls-hist-row{display:flex;gap:7px;font-size:11px;padding:2px 0}
.ls-hist-k{color:var(--cc);min-width:95px;flex-shrink:0}
.ls-hist-v{color:var(--ch);font-weight:600}
.ls-data-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:9px}
.ls-data-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px}
.ls-data-lbl{font-size:10px;font-weight:600;color:var(--cc);text-transform:uppercase;letter-spacing:.04em;margin:0 0 4px}
.ls-data-val{font-size:13px;font-weight:700;color:var(--ch);margin:0 0 2px}
.ls-data-val-g{color:var(--ok500)}
.ls-data-val-o{color:var(--warn500)}
.ls-data-sub{font-size:10px;color:var(--cc);margin:0 0 5px}
.ls-data-link{font-size:10px;font-weight:600;color:var(--p700);cursor:pointer;background:none;border:none;padding:0;font-family:var(--ff)}
.ls-insight-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:11px}
.ls-ins-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:13px;display:flex;flex-direction:column;gap:5px;transition:var(--tr-f)}
.ls-ins-card:hover{border-color:var(--p300);box-shadow:var(--sh-xs)}
.ls-ins-icon{width:34px;height:34px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:16px;margin-bottom:3px}
.ls-ins-title{font-size:12px;font-weight:700;color:var(--p700);margin:0 0 2px;line-height:1.3}
.ls-ins-desc{font-size:10px;color:var(--cb);margin:0;line-height:1.4;flex:1}
.ls-ins-btn{display:inline-flex;align-items:center;gap:4px;padding:4px 9px;background:transparent;color:var(--p700);border:1px solid var(--p300);border-radius:var(--r-sm);font-size:10px;font-weight:600;cursor:pointer;margin-top:3px;transition:var(--tr-f);font-family:var(--ff)}
.ls-ins-btn:hover{background:var(--p100)}
@media(max-width:1100px){.ls-conf-row{grid-template-columns:repeat(3,1fr)}.ls-two{grid-template-columns:1fr}.ls-data-grid{grid-template-columns:repeat(3,1fr)}.ls-insight-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:768px){.ls-conf-row{grid-template-columns:repeat(2,1fr)}.ls-data-grid{grid-template-columns:repeat(2,1fr)}.ls-hist-table{grid-template-columns:1fr}}
@media(max-width:480px){.ls-data-grid{grid-template-columns:1fr 1fr}.ls-insight-grid{grid-template-columns:1fr}}
`;

const JOURNEY_STAGES = [
  {icon:"👧",label:"Adolescence",done:true},{icon:"🌸",label:"Young Adult",done:true},
  {icon:"💜",label:"Reproductive Age",active:true},{icon:"🤰",label:"Pregnancy",muted:true},
  {icon:"👶",label:"Postpartum",muted:true},{icon:"🌺",label:"Perimenopause",muted:true},
  {icon:"🍂",label:"Menopause",muted:true},{icon:"🌟",label:"Senior Women's Health",muted:true},
];

function LifeStageScreen() {
  const [tab, setTab] = useState("Overview");
  const TABS = ["Overview","Menstrual Health","Reproductive Health","Fertility","Assessments","Preventive Care","Lifestyle","Notes"];
  return (
    <div className="page-wrap">
      <div className="page-head">
        <div><h1 className="page-title">Life Stage Details</h1><p className="page-sub">Dynamic classification, transition signals, and care focus tailored to the patient's current stage.</p></div>
        <div className="head-actions"><button className="btn-outline">✏️ Update Stage</button><button className="btn-primary">+ Add Note</button></div>
      </div>
      <PatientHeader summaryLabel="Record Summary" summaryFields={[{label:"BMI",value:"25.9 (Overweight)"},{label:"Last Visit",value:"12 May 2025"},{label:"Care Team",value:"Dr. Ananya Sharma & 1 more"}]} />

      {/* Confidence row */}
      <div className="ls-conf-row">
        <div className="ls-conf-card">
          <p className="ls-conf-lbl">Current Life Stage</p>
          <p className="ls-conf-val-p">Reproductive Age</p>
          <p style={{fontSize:11,fontWeight:700,color:"var(--s500)",margin:"3px 0 0"}}>Preconception / Fertility Readiness</p>
        </div>
        <div className="ls-conf-card">
          <p className="ls-conf-lbl">Confidence Score</p>
          <p className="ls-conf-val-g">High</p>
          <div className="ls-prog-bar"><div className="ls-prog-fill" style={{width:"92%"}} /></div>
          <p className="ls-conf-sub">92%</p>
        </div>
        <div className="ls-conf-card">
          <p className="ls-conf-lbl">Transition Signals</p>
          {["Ovulatory cycles","Stable ± 2–4 days","Actively planning pregnancy"].map((t,i) => <p key={i} className="ls-conf-sub" style={{margin:"2px 0"}}>• {t}</p>)}
          <span className="ls-tag">Ongoing</span>
        </div>
        <div className="ls-conf-card">
          <p className="ls-conf-lbl">Stage Since</p>
          <p className="ls-conf-val-p">Jan 2023</p>
          <p className="ls-conf-sub">1 year 4 months</p>
        </div>
        <div className="ls-conf-card">
          <p className="ls-conf-lbl">Next Likely Stage</p>
          <p className="ls-conf-val-p">Pregnancy</p>
          <span className="badge-orange">Moderate</span>
          <p className="ls-conf-sub" style={{marginTop:4}}>In next 6–12 months</p>
        </div>
      </div>

      {/* Journey timeline */}
      <div className="card">
        <h2 className="sec-title" style={{marginBottom:12}}>Women's Life Stage Journey</h2>
        <div className="ls-journey">
          {JOURNEY_STAGES.map((st,i) => (
            <div key={i} className="ls-j-item">
              {i < JOURNEY_STAGES.length-1 && <div className={`ls-j-line${st.done?" done":""}`} />}
              <div className={`ls-j-dot${st.active?" active":st.done?" done":st.muted?" muted":""}`}>{st.icon}</div>
              <p className={`ls-j-lbl${st.active?" active":st.muted?" muted":""}`}>{st.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Two panels */}
      <div className="ls-two">
        <div className="ls-panel">
          <p className="ls-panel-title"><span className="ls-panel-icon">📖</span>Stage Definition / Overview</p>
          <p style={{fontSize:11,color:"var(--cb)",lineHeight:1.6,marginBottom:12}}>Reproductive Age encompasses the years when women are physically capable of becoming pregnant. This phase focuses on reproductive health, cycle regularity, fertility optimization, and preparation for a healthy pregnancy.</p>
          <p className="ls-panel-title" style={{fontSize:12,margin:"0 0 7px"}}>Key Physiological Characteristics</p>
          {["Regular ovulatory menstrual cycles","Peak fertility window (ideal conception years)","Hormonal balance with estrogen & progesterone cycling","Optimal time for family planning and preconception care"].map((b,i) => (
            <div key={i} className="ls-bullet"><span className="ls-dot" />{b}</div>
          ))}
          <p className="ls-panel-title" style={{fontSize:12,margin:"12px 0 7px"}}>Typical Care Priorities</p>
          {["Fertility awareness & preconception optimization","Cycle health, ovulation tracking & nutrition","Mental well-being & stress management","Immunizations & genetic risk assessment"].map((b,i) => (
            <div key={i} className="ls-bullet"><span style={{color:"var(--ok500)"}}>✓</span>{b}</div>
          ))}
        </div>
        <div className="ls-panel">
          <p className="ls-panel-title"><span className="ls-panel-icon">📊</span>Stage-specific Symptoms & Indicators</p>
          {["Mild cyclic cramping","Breast tenderness (luteal phase)","Ovulation signs (cervical mucus, mild mid-cycle pain)","Energy & mood variations across cycle"].map((b,i) => (
            <div key={i} className="ls-bullet"><span className="ls-dot" style={{background:"var(--ok500)"}} />{b}</div>
          ))}
          <p className="ls-panel-title" style={{fontSize:12,margin:"12px 0 7px"}}>⚠️ Risk Flags & Watchouts</p>
          {["Irregular cycles (>35 days or <21 days)","History of PCOS or insulin resistance","Thyroid dysfunction","Vitamin D deficiency","BMI outside healthy range"].map((b,i) => (
            <div key={i} className="ls-warn"><span style={{color:"var(--warn500)",fontSize:12}}>▲</span>{b}</div>
          ))}
          <p className="ls-panel-title" style={{fontSize:12,margin:"12px 0 7px"}}>AI-Generated Next Best Actions</p>
          {["Complete fertility readiness assessment","Optimize nutrition, folic acid & lifestyle","Screen for thyroid & metabolic risks","Track ovulation & cycle regularity"].map((b,i) => (
            <div key={i} className="ls-bullet"><span className="ls-dot" style={{background:"var(--s500)"}} />{b}</div>
          ))}
        </div>
      </div>

      {/* Relevant history */}
      <div className="card">
        <p className="sec-title" style={{marginBottom:11}}>Relevant History and Context</p>
        <div className="ls-hist-table">
          {[{k:"Menarche Age",v:"13 years"},{k:"Goush...",v:"Yes"},{k:"Cycle Pattern",v:"Regular (28–30 days)"},{k:"Contraception",v:"None (Actively trying)"},{k:"Cycle Length",v:"29 ± 2 days"},{k:"Gravida / Para",v:"G0 P0"}].map((r,i) => (
            <div key={i} className="ls-hist-row"><span className="ls-hist-k">{r.k}</span><span className="ls-hist-v">{r.v}</span></div>
          ))}
        </div>
      </div>

      {/* Stage data */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Stage-Specific Data</h2></div>
        <div className="tab-bar">
          {TABS.map(t => <button key={t} className={`tab${tab===t?" active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
        </div>
        {tab === "Overview" ? (
          <div className="ls-data-grid">
            {[
              {icon:"📋",lbl:"Menstrual Profile",val:"Regular",sub:"29 ± 2 days\nDuration: 4–5 days",link:"View Details"},
              {icon:"💜",lbl:"Fertility Intent",val:"Actively Trying",sub:"Trying since: Jan 2025\nGoal: Pregnancy in 6 months",link:"Update",valCls:"ls-data-val-g"},
              {icon:"🛡️",lbl:"Contraception History",val:"None",sub:"Previously: OCP (3 yrs ago)\nCurrent: Not using",link:"View History"},
              {icon:"🤰",lbl:"Obstetric History",val:"G0 P0",sub:"No prior pregnancies\nNo pregnancy loss",link:"Add History"},
              {icon:"⚠️",lbl:"Active Concerns",val:"None Reported",sub:"Feels well overall",link:"Add Concern"},
              {icon:"🔍",lbl:"Preventive Gaps",val:"TSH Screening",sub:"Vitamin D Test\nRubella Immunity",link:"Address Now",valCls:"ls-data-val-o"},
            ].map((d,i) => (
              <div key={i} className="ls-data-card">
                <p className="ls-data-lbl">{d.icon} {d.lbl}</p>
                <p className={`ls-data-val${d.valCls?" "+d.valCls:""}`}>{d.val}</p>
                {d.sub.split("\n").map((s,j) => <p key={j} className="ls-data-sub">{s}</p>)}
                <button className="ls-data-link">{d.link}</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{padding:"28px",textAlign:"center",color:"var(--cc)",fontSize:13}}><div style={{fontSize:28,marginBottom:8}}>📋</div><p>{tab} data available here.</p></div>
        )}
      </div>

      {/* AI Insights */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">AI Life Stage Insights <span style={{color:"var(--s500)"}}>✨</span></h2></div>
        <div className="ls-insight-grid">
          {[
            {icon:"📋",title:"Complete fertility readiness assessment",desc:"Evaluate key biomarkers and optimize factors for conception.",btn:"Start Assessment >"},
            {icon:"🧪",title:"Order TSH and HbA1c screening",desc:"Early detection helps improve fertility outcomes.",btn:"Order Tests >"},
            {icon:"🥗",title:"Start preconception nutrition plan",desc:"Personalized nutrition to support fertility & pregnancy health.",btn:"View Plan >"},
            {icon:"📅",title:"Book gynecology consultation",desc:"Discuss cycle tracker data and preconception plan.",btn:"Book Appointment >"},
          ].map((ins,i) => (
            <div key={i} className="ls-ins-card">
              <div className="ls-ins-icon">{ins.icon}</div>
              <p className="ls-ins-title">{ins.title}</p>
              <p className="ls-ins-desc">{ins.desc}</p>
              <button className="ls-ins-btn">{ins.btn}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Care Plan */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Care Plan & Milestones</h2></div>
        <CarePlanSection followup={{date:"01 Jun 2025, 10:30 AM",doctor:"Dr. Ananya Sharma",specialty:"Gynecologist",clinic:"Avexa Women's Health Clinic, Mumbai"}} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   CLINICAL HISTORY SCREEN
───────────────────────────────────────────────────── */
const clinicalCSS = `
.ch-qs{display:grid;grid-template-columns:repeat(5,1fr);gap:9px;margin-bottom:14px}
.ch-qs-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px;display:flex;align-items:flex-start;gap:9px}
.ch-qs-icon{width:30px;height:30px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}
.ch-qs-title{font-size:10px;color:var(--cc);font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin:0 0 3px}
.ch-qs-item{font-size:11px;color:var(--cb);padding:1px 0}
.ch-prob-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.ch-prob-row{display:flex;align-items:flex-start;gap:9px;padding:10px 12px;background:#fff;border:1px solid var(--border);border-radius:var(--r-md)}
.ch-prob-icon{width:28px;height:28px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0}
.ch-prob-title{font-size:12px;font-weight:700;color:var(--ch);margin:0 0 4px}
.ch-prob-item{display:flex;align-items:center;gap:5px;font-size:11px;color:var(--cb);padding:1px 0}
.ch-prob-dot{width:5px;height:5px;border-radius:50%;background:var(--p400);flex-shrink:0}
.ch-med-row{display:flex;align-items:center;gap:10px;padding:6px 0;border-bottom:1px solid var(--g100)}
.ch-med-row:last-of-type{border-bottom:none}
.ch-med-dot{width:7px;height:7px;border-radius:50%;background:var(--ok500);flex-shrink:0}
.ch-fam-row{display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid var(--g100);font-size:11px}
.ch-fam-row:last-child{border-bottom:none}
.ch-enc-row{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--g100)}
.ch-enc-row:last-child{border-bottom:none}
.ch-enc-icon{width:30px;height:30px;border-radius:50%;background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}
.ch-insight-bar{display:grid;grid-template-columns:repeat(3,1fr);gap:11px}
.ch-ins-box{border-radius:var(--r-md);padding:13px}
.ch-ins-box-r{background:var(--err100);border:1px solid #FECACA}
.ch-ins-box-p{background:var(--info100);border:1px solid var(--p200)}
.ch-ins-box-o{background:var(--warn100);border:1px solid #FDE68A}
.ch-ins-title{font-size:11px;font-weight:700;margin:0 0 6px;display:flex;align-items:center;gap:4px}
.ch-ins-item{font-size:10px;color:var(--cb);padding:2px 0}
@media(max-width:1100px){.ch-qs{grid-template-columns:repeat(3,1fr)}.ch-insight-bar{grid-template-columns:1fr}}
@media(max-width:768px){.ch-qs{grid-template-columns:repeat(2,1fr)}.ch-prob-grid{grid-template-columns:1fr}}
@media(max-width:480px){.ch-qs{grid-template-columns:1fr 1fr}}
`;

const clinTimeline = [
  {icon:"👧",label:"Menarche",sub:"Age 13",done:true},{icon:"📋",label:"Irregular Cycles",sub:"Age 19",done:true},
  {icon:"⚕️",label:"PCOS Diagnosed",sub:"2020",done:true},{icon:"💊",label:"Started Metformin",sub:"2021",done:true},
  {icon:"💜",label:"Fertility Planning",sub:"2024",done:true},{icon:"🌸",label:"Preconception Care",sub:"2026",active:true},
];

function ClinicalHistoryScreen() {
  return (
    <div className="page-wrap">
      <div className="page-head">
        <div><h1 className="page-title">Clinical History</h1><p className="page-sub">Longitudinal clinical context, conditions, interventions, and family history across the patient's care journey.</p></div>
        <div className="head-actions"><button className="btn-outline">✏️ Update History</button><button className="btn-primary">+ Add Note</button></div>
      </div>
      <PatientHeader summaryLabel="Clinical Summary" summaryFields={[{label:"BMI",value:"25.9 (Overweight)"},{label:"Last Visit",value:"12 May 2026"},{label:"Primary Physician",value:"Dr. Ananya Sharma"}]} />

      {/* Quick stats */}
      <div className="ch-qs">
        {[
          {icon:"❤️",title:"Active Conditions",items:["PCOS","Irregular Periods","Vitamin D Deficiency"]},
          {icon:"⚠️",title:"Allergies",items:["Penicillin","No food allergies"]},
          {icon:"🔬",title:"Past Procedures",items:["Pelvic Ultrasound","Hormonal Panel"]},
          {icon:"👨‍👩‍👧",title:"Family History",items:["Diabetes","Thyroid Disorder"]},
          {icon:"🚨",title:"Risk Context",items:["Moderate metabolic risk","Thyroid screening due"]},
        ].map((q,i) => (
          <div key={i} className="ch-qs-card">
            <div className="ch-qs-icon">{q.icon}</div>
            <div>
              <p className="ch-qs-title">{q.title}</p>
              {q.items.map((item,j) => <p key={j} className="ch-qs-item">• {item}</p>)}
            </div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="card">
        <h2 className="sec-title" style={{marginBottom:12}}>Clinical Journey Timeline</h2>
        <HealthTimeline events={clinTimeline} />
      </div>

      {/* Problem list */}
      <div className="two-col">
        <div className="left-col">
          <div className="card">
            <h2 className="sec-title" style={{marginBottom:12}}>Problem List & Medical History</h2>
            <div className="ch-prob-grid">
              <div style={{display:"flex",flexDirection:"column",gap:9}}>
                {[
                  {icon:"❤️",title:"Active Conditions",items:["PCOS — Moderate","Irregular Menstrual Cycles","Vitamin D Deficiency","Anemia (resolved)","Acne","Mild insulin resistance"]},
                  {icon:"📝",title:"Past Medical History",items:[]},
                  {icon:"✂️",title:"Surgical / Procedure History",items:["No major surgery","Pelvic ultrasound completed","Hormonal investigations completed"]},
                  {icon:"🏥",title:"Hospitalizations",items:["None reported"]},
                ].map((sec,i) => (
                  <div key={i} className="ch-prob-row">
                    <div className="ch-prob-icon">{sec.icon}</div>
                    <div style={{flex:1}}>
                      <p className="ch-prob-title">{sec.title}</p>
                      {sec.items.length > 0 ? sec.items.map((it,j) => (
                        <div key={j} className="ch-prob-item"><span className="ch-prob-dot" />{it}{it.includes("Moderate")&&<span className="badge-orange" style={{marginLeft:4}}>Moderate</span>}</div>
                      )) : <span style={{fontSize:11,color:"var(--cc)",fontStyle:"italic"}}>No records</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:9}}>
                <div className="ch-prob-row">
                  <div className="ch-prob-icon">🔬</div>
                  <div style={{flex:1}}>
                    <p className="ch-prob-title">Gynecological History</p>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"2px 10px",marginTop:4}}>
                      {[{k:"Menarche",v:"Age 13"},{k:"Regularity",v:"Irregular (28–35 days)"},{k:"Dysmenorrhea",v:"Mild"},{k:"Sexually Active",v:"Yes"}].map((r,i) => (
                        <div key={i} style={{fontSize:11,padding:"2px 0"}}><span style={{color:"var(--cc)"}}>{r.k}: </span><span style={{color:"var(--ch)",fontWeight:600}}>{r.v}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="ch-prob-row">
                  <div className="ch-prob-icon">🤰</div>
                  <div><p className="ch-prob-title">Obstetric History</p><p style={{fontSize:12,fontWeight:600,color:"var(--ch)"}}>G0 P0</p><p style={{fontSize:10,color:"var(--cc)"}}>No prior pregnancies</p></div>
                </div>
              </div>
            </div>
          </div>
          {/* Clinical insights */}
          <div className="card">
            <h2 className="sec-title" style={{marginBottom:12}}>Clinical Insights</h2>
            <div className="ch-insight-bar">
              <div className="ch-ins-box ch-ins-box-r">
                <p className="ch-ins-title" style={{color:"var(--err500)"}}>⚠️ Key Clinical Alerts</p>
                {["Irregular cycles persist","Low vitamin D","Possible metabolic risk"].map((a,i) => <div key={i} className="ch-ins-item">• {a}</div>)}
              </div>
              <div className="ch-ins-box ch-ins-box-p">
                <p className="ch-ins-title" style={{color:"var(--p700)"}}>📋 Recommended Evaluations</p>
                {["TSH, HbA1c","AMH","Pelvic ultrasound if indicated"].map((a,i) => <div key={i} className="ch-ins-item">• {a}</div>)}
              </div>
              <div className="ch-ins-box ch-ins-box-o">
                <p className="ch-ins-title" style={{color:"var(--warn500)"}}>📅 Care Continuity Gaps</p>
                {["Pap smear due","Thyroid profile due"].map((a,i) => <div key={i} className="ch-ins-item">• {a}</div>)}
              </div>
            </div>
          </div>
        </div>

        <div className="right-col">
          {/* Medications */}
          <div className="card">
            <h2 className="sec-title" style={{marginBottom:10}}>💊 Medications & Allergies</h2>
            <p style={{fontSize:12,fontWeight:700,color:"var(--ok500)",margin:"0 0 8px"}}>Current Medications</p>
            {[{n:"Metformin 500 mg",d:"1 tab BD"},{n:"Vitamin D3 60,000 IU",d:"Weekly"},{n:"Folic Acid 5 mg",d:"1 tab OD"}].map((m,i) => (
              <div key={i} className="ch-med-row">
                <div className="ch-med-dot" />
                <span style={{flex:1,fontSize:12,fontWeight:600,color:"var(--ch)"}}>{m.n}</span>
                <span style={{fontSize:11,color:"var(--cb)"}}>{m.d}</span>
              </div>
            ))}
            <div style={{display:"inline-flex",alignItems:"center",gap:4,background:"var(--ok100)",color:"var(--ok500)",borderRadius:"var(--r-full)",padding:"2px 8px",fontSize:10,fontWeight:600,marginTop:8}}>✓ Adherence: Good</div>
            <div className="divider" />
            <p style={{fontSize:12,fontWeight:700,color:"var(--err500)",margin:"0 0 6px"}}>Allergies</p>
            {["Penicillin — rash","No known food allergies"].map((a,i) => (
              <div key={i} style={{fontSize:11,color:"var(--cb)",padding:"2px 0",display:"flex",alignItems:"center",gap:6}}>
                <span style={{color:"var(--err500)"}}>●</span> {a}
              </div>
            ))}
          </div>

          {/* Family history */}
          <div className="card">
            <h2 className="sec-title" style={{marginBottom:10}}>👨‍👩‍👧 Family History & Genetic Risk</h2>
            {[{m:"Mother",c:"Type 2 Diabetes"},{m:"Sister",c:"Thyroid Disorder"},{m:"Maternal aunt",c:"PCOS symptoms"},{m:"Breast/Ovarian cancer history",c:"None reported"}].map((f,i) => (
              <div key={i} className="ch-fam-row">
                <span style={{color:"var(--cb)",flex:1}}>{f.m}</span>
                <span style={{color:"var(--ch)",fontWeight:600,textAlign:"right"}}>{f.c}</span>
              </div>
            ))}
            <div style={{display:"inline-flex",alignItems:"center",gap:5,background:"var(--ok100)",color:"var(--ok500)",borderRadius:"var(--r-full)",padding:"4px 12px",fontSize:11,fontWeight:600,marginTop:9}}>✓ No high hereditary cancer flags identified</div>
          </div>

          {/* Encounters */}
          <div className="card">
            <h2 className="sec-title" style={{marginBottom:10}}>Recent Encounters & Provider Notes</h2>
            {[
              {icon:"🩺",date:"12 May 2026",doc:"Dr. Ananya Sharma",note:"Routine gynecology review. Cycles irregular; weight concerns."},
              {icon:"🥗",date:"20 Apr 2026",doc:"Ms. Riya Malhotra",note:"Nutrition counseling. Focus on insulin balance diet."},
              {icon:"💜",date:"05 Feb 2026",doc:"Dr. Ananya Sharma",note:"Fertility readiness consult. PCOS management plan discussed."},
            ].map((enc,i) => (
              <div key={i} className="ch-enc-row">
                <div className="ch-enc-icon">{enc.icon}</div>
                <div style={{minWidth:80,fontSize:11,fontWeight:600,color:"var(--ch)"}}>{enc.date}</div>
                <div style={{flex:1,fontSize:11,fontWeight:500,color:"var(--ch)"}}>{enc.doc}</div>
                <div style={{flex:2,fontSize:11,color:"var(--cb)"}}>{enc.note}</div>
                <span style={{color:"var(--p400)",fontSize:14,cursor:"pointer"}}>›</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Care Plan */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Care Plan & Follow-up</h2></div>
        <CarePlanSection
          goals={["Improve cycle regularity","Optimize preconception health","Manage weight"]}
          programs={[{name:"Preconception Care Program",status:"Active"},{name:"Nutrition & Lifestyle Coaching",status:"Active"}]}
          tasks={[{name:"Complete TSH test",due:"Due on 18 May 2026"},{name:"Submit Day-2 hormone panel",due:"Due on 23 May 2026"},{name:"Follow-up consult",due:"Due on 01 Jun 2026"}]}
          followup={{date:"01 Jun 2026, 10:30 AM",doctor:"Dr. Ananya Sharma",specialty:"Gynecologist",clinic:"Avexa Women's Health Clinic, Mumbai"}}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   WOMEN'S HEALTH SCREEN
───────────────────────────────────────────────────── */
const womensHealthCSS = `
.wh-info-bar{display:grid;grid-template-columns:repeat(6,1fr);gap:9px;margin-bottom:14px}
.wh-info-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px 13px;display:flex;flex-direction:column;gap:2px}
.wh-info-title{font-size:10px;color:var(--cc);font-weight:600;text-transform:uppercase;letter-spacing:.05em;margin:0 0 4px;display:flex;align-items:center;gap:4px}
.wh-info-val{font-size:12px;font-weight:700;color:var(--ch);margin:0 0 2px;line-height:1.3}
.wh-info-sub{font-size:10px;color:var(--cc)}
.wh-info-link{font-size:10px;font-weight:600;color:var(--p700);cursor:pointer;background:none;border:none;padding:0;margin-top:4px;font-family:var(--ff)}
.wh-mens-grid{display:grid;grid-template-columns:repeat(8,1fr);gap:8px}
.wh-field{display:flex;flex-direction:column;gap:2px}
.wh-field-lbl{font-size:10px;color:var(--cc);font-weight:500}
.wh-field-val{font-size:12px;font-weight:600;color:var(--ch)}
.wh-symp-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:8px}
.wh-symp-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:10px;display:flex;flex-direction:column;gap:4px}
.wh-symp-name{font-size:11px;font-weight:600;color:var(--ch)}
.sparkline-wrap{height:22px;display:flex;align-items:center}
.wh-horm-grid{display:grid;grid-template-columns:repeat(8,1fr);gap:8px}
.wh-lab-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:10px 11px}
.wh-lab-name{font-size:10px;color:var(--cc);font-weight:600;margin:0 0 3px}
.wh-lab-val{font-size:14px;font-weight:700;color:var(--ch);margin:0 0 1px}
.wh-lab-unit{font-size:10px;color:var(--cc)}
.wh-prev-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:9px}
.wh-prev-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px;display:flex;flex-direction:column;align-items:center;gap:5px;text-align:center}
.wh-prev-icon{width:34px;height:34px;border-radius:50%;background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:15px}
.wh-prev-name{font-size:11px;font-weight:600;color:var(--ch);line-height:1.3}
.wh-assess-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:9px}
.wh-assess-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:11px}
.wh-assess-title{font-size:11px;font-weight:600;color:var(--ch);margin:0 0 4px;line-height:1.3}
.wh-assess-score{font-size:17px;font-weight:700;color:var(--p700);margin:0 0 2px}
.wh-assess-sub{font-size:10px;color:var(--cc)}
.wh-ai-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:10px}
.wh-ai-card{background:#fff;border:1px solid var(--border);border-radius:var(--r-md);padding:12px;display:flex;flex-direction:column;gap:5px;transition:var(--tr-f)}
.wh-ai-card:hover{border-color:var(--p300);box-shadow:var(--sh-xs)}
.wh-ai-icon{width:34px;height:34px;border-radius:var(--r-sm);background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:16px}
.wh-ai-title{font-size:12px;font-weight:700;color:var(--p700);margin:0 0 2px;line-height:1.3}
.wh-ai-desc{font-size:10px;color:var(--cb);margin:0;line-height:1.4;flex:1}
.wh-ai-btn{display:inline-flex;align-items:center;gap:3px;padding:4px 9px;background:transparent;color:var(--p700);border:1px solid var(--p300);border-radius:var(--r-sm);font-size:10px;font-weight:600;cursor:pointer;margin-top:3px;transition:var(--tr-f);font-family:var(--ff);width:fit-content}
.wh-ai-btn:hover{background:var(--p100)}
.wh-repro-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:9px}
@media(max-width:1100px){.wh-info-bar{grid-template-columns:repeat(3,1fr)}.wh-mens-grid{grid-template-columns:repeat(4,1fr)}.wh-symp-grid{grid-template-columns:repeat(3,1fr)}.wh-horm-grid{grid-template-columns:repeat(4,1fr)}.wh-assess-grid{grid-template-columns:repeat(3,1fr)}.wh-ai-grid{grid-template-columns:repeat(3,1fr)}.wh-prev-grid{grid-template-columns:repeat(3,1fr)}}
@media(max-width:768px){.wh-info-bar{grid-template-columns:repeat(2,1fr)}.wh-mens-grid{grid-template-columns:repeat(2,1fr)}.wh-symp-grid{grid-template-columns:repeat(2,1fr)}.wh-horm-grid{grid-template-columns:repeat(2,1fr)}.wh-assess-grid{grid-template-columns:repeat(2,1fr)}.wh-ai-grid{grid-template-columns:repeat(2,1fr)}.wh-repro-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:480px){.wh-ai-grid{grid-template-columns:1fr}}
`;

const wCycleTimeline = [
  {icon:"👧",label:"Menarche",sub:"Age 13",done:true},{icon:"📋",label:"Irregular Cycles",sub:"Since Age 19",done:true},
  {icon:"⚕️",label:"PCOS Diagnosis",sub:"2020",done:true},{icon:"🌸",label:"Fertility Planning",sub:"Since Jan 2025",active:true},
  {icon:"🤰",label:"Preconception Care",sub:"Upcoming",muted:true},
];

function Sparkline({ color="#9C27B0", up=true }) {
  const pts = up ? "0,20 10,16 20,18 30,12 40,14 50,8 60,10 70,6" : "0,8 10,12 20,10 30,14 40,12 50,16 60,14 70,18";
  return <svg width="70" height="22" viewBox="0 0 70 22" style={{width:"100%",height:22}}><polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}

function WomensHealthScreen() {
  return (
    <div className="page-wrap">
      <div className="page-head">
        <div><h1 className="page-title">Women's Health</h1><p className="page-sub">A longitudinal, life-stage-aware view of menstrual, reproductive, fertility, gynecological, hormonal, and preventive health.</p></div>
        <div className="head-actions"><button className="btn-outline">✏️ Update Record</button><button className="btn-primary">+ Add Note</button></div>
      </div>
      <PatientHeader summaryLabel="Women's Health Summary" summaryFields={[{label:"Menstrual Cycle",value:"Irregular (35–45 days)"},{label:"Cycle Length",value:"Avg 41 days"},{label:"Last Period",value:"05 May 2026"},{label:"Fertility Intent",value:"TTC in next 6 months"}]} />

      {/* Info bar */}
      <div className="wh-info-bar">
        {[
          {icon:"📋",title:"Menstrual Profile",val:"Irregular",sub:"35–45 day cycles\nDuration: 4–5 days",link:"View Details"},
          {icon:"💜",title:"Fertility Intent",val:"Trying to Conceive",sub:"Since Jan 2025\nGoal: 6 months",link:"Update Intent"},
          {icon:"🛡️",title:"Contraception Status",val:"None",sub:"Actively trying\nNo hormonal contraception",link:"View History"},
          {icon:"🤰",title:"Obstetric History",val:"G0 P0",sub:"No prior pregnancies\nNo miscarriages",link:"Add History"},
          {icon:"⚠️",title:"Hormonal / Metabolic Risk",val:"Moderate",sub:"PCOS risk: Moderate\nIR risk: Moderate",link:"View Details",orange:true},
          {icon:"✅",title:"Preventive Care Status",val:"Up to date",sub:"Pap: Due May 2026\nThyroid: Due Jun 2026",link:"View Care",green:true},
        ].map((c,i) => (
          <div key={i} className="wh-info-card">
            <p className="wh-info-title"><span>{c.icon}</span>{c.title}</p>
            <p className="wh-info-val" style={c.orange?{color:"var(--warn500)"}:c.green?{color:"var(--ok500)"}:{}}>{c.val}</p>
            {c.sub.split("\n").map((s,j) => <p key={j} className="wh-info-sub">{s}</p>)}
            <button className="wh-info-link">{c.link}</button>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div className="card">
        <h2 className="sec-title" style={{marginBottom:12}}>Cycle & Reproductive Overview</h2>
        <HealthTimeline events={wCycleTimeline} />
      </div>

      {/* Menstrual + Symptoms */}
      <div className="two-col">
        <div className="card" style={{marginBottom:0}}>
          <div className="sec-head"><h2 className="sec-title">🩸 Menstrual Health</h2></div>
          <div className="wh-mens-grid">
            {[{l:"Cycle Length",v:"35–45 days"},{l:"Duration",v:"4–5 days"},{l:"Last Period",v:"05 May 2026"},{l:"Next Due",v:"09–16 Jun 2026"},{l:"Regularity",v:"Irregular"},{l:"Flow",v:"Moderate 🩸🩸"},{l:"PMS Symptoms",v:"Bloating, Irritability"},{l:"Dysmenorrhea",v:"Mild cramps"}].map((f,i) => (
              <div key={i} className="wh-field"><span className="wh-field-lbl">{f.l}</span><span className="wh-field-val">{f.v}</span></div>
            ))}
          </div>
        </div>
        <div className="card" style={{marginBottom:0}}>
          <div className="sec-head"><h2 className="sec-title">🔴 Symptoms & Concerns</h2><button className="sec-link">View Trends</button></div>
          <div className="wh-symp-grid">
            {[
              {n:"Acne",lvl:"Moderate",cls:"badge-orange",up:false},{n:"Hair Fall",lvl:"Moderate",cls:"badge-orange",up:false},
              {n:"Fatigue",lvl:"Mild",cls:"badge-green",up:true},{n:"Bloating",lvl:"Moderate",cls:"badge-orange",up:false},
              {n:"Pelvic Pain",lvl:"Mild",cls:"badge-green",up:true},{n:"Mood Swings",lvl:"Mild",cls:"badge-green",up:true},
            ].map((sym,i) => (
              <div key={i} className="wh-symp-card">
                <p className="wh-symp-name">{sym.n}</p>
                <span className={sym.cls}>{sym.lvl}</span>
                <div className="sparkline-wrap"><Sparkline color={sym.up?"#16a34a":"#f59e0b"} up={sym.up} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Repro + Hormonal */}
      <div className="two-col" style={{marginTop:14}}>
        <div className="card" style={{marginBottom:0}}>
          <div className="sec-head"><h2 className="sec-title">💜 Reproductive & Fertility</h2></div>
          <div className="wh-repro-grid">
            {[{l:"Trying to Conceive",v:"Since Jan 2025"},{l:"Ovulation Window",v:"May 20 – May 24"},{l:"Gravida / Para",v:"G0 P0"},{l:"Fertility Goal",v:"Pregnancy in 6 months"}].map((f,i) => (
              <div key={i} className="wh-field"><span className="wh-field-lbl">{f.l}</span><span className="wh-field-val">{f.v}</span></div>
            ))}
          </div>
          <div className="divider" />
          <div className="sec-head" style={{marginBottom:9}}><h2 className="sec-title" style={{fontSize:12}}>🔬 Gynecological Health</h2></div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {[{l:"PCOS Status",v:"Diagnosed (2020)"},{l:"Pelvic Pain",v:"Occasional"},{l:"Abnormal Discharge",v:"None"},{l:"Prior Procedures",v:"None"},{l:"Last Pelvic Scan",v:"Dec 2024 (Normal)"},{l:"USG Findings",v:"Normal uterus & ovaries"},{l:"Fibroids / Cysts",v:"No"},{l:"Endometriosis",v:"Not evaluated"}].map((f,i) => (
              <div key={i} className="wh-field"><span className="wh-field-lbl">{f.l}</span><span className="wh-field-val">{f.v}</span></div>
            ))}
          </div>
        </div>
        <div className="card" style={{marginBottom:0}}>
          <div className="sec-head"><h2 className="sec-title">🧬 Hormonal & Metabolic</h2><button className="sec-link">View Labs</button></div>
          <div className="wh-horm-grid">
            {[
              {n:"BMI",v:"25.9",u:"Overweight",o:true},{n:"HbA1c",v:"5.6 %",u:"Normal"},{n:"TSH",v:"2.45 μIU/mL",u:"Normal"},{n:"Vitamin D",v:"18 ng/mL",u:"Low",o:true},
              {n:"IR Risk",v:"Moderate",u:"",o:true},{n:"Testosterone",v:"48 ng/dL",u:"Normal"},{n:"AMH",v:"4.1 ng/mL",u:"High-Normal"},{n:"Prolactin",v:"14.2 ng/mL",u:"Normal"},
            ].map((lb,i) => (
              <div key={i} className="wh-lab-card">
                <p className="wh-lab-name">{lb.n}</p>
                <p className="wh-lab-val" style={lb.o?{color:"var(--warn500)"}:{}}>{lb.v}</p>
                <p className="wh-lab-unit">{lb.u}</p>
              </div>
            ))}
          </div>
          <div className="divider" />
          <div className="sec-head" style={{marginBottom:9}}><h2 className="sec-title" style={{fontSize:12}}>🛡️ Preventive Care</h2></div>
          <div className="wh-prev-grid">
            {[{icon:"🩺",n:"Pap Smear",d:"Due May 2026",cls:"badge-red"},{icon:"💉",n:"HPV Vaccine",d:"Not Received",cls:"badge-orange"},{icon:"🦋",n:"Thyroid Screening",d:"Due Jun 2026",cls:"badge-red"},{icon:"🥗",n:"Nutrition Consult",d:"Done Dec 2024",cls:"badge-green"},{icon:"🔬",n:"Pelvic Ultrasound",d:"Not Due",cls:"badge-green"}].map((p,i) => (
              <div key={i} className="wh-prev-card">
                <div className="wh-prev-icon">{p.icon}</div>
                <p className="wh-prev-name">{p.n}</p>
                <span className={p.cls}>{p.d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Assessments */}
      <div className="card" style={{marginTop:14}}>
        <div className="sec-head"><h2 className="sec-title">📊 Stage-Specific Assessments & Insights</h2></div>
        <div className="wh-assess-grid">
          {[
            {icon:"🔵",t:"PCOS Assessment",s:58,max:100,l:"Moderate Risk",o:true},
            {icon:"😔",t:"PHQ-9 (Depression)",s:6,max:27,l:"Mild"},
            {icon:"😰",t:"GAD-7 (Anxiety)",s:5,max:21,l:"Mild"},
            {icon:"💜",t:"Fertility Readiness",s:72,max:100,l:"Good",g:true},
            {icon:"🦋",t:"Thyroid Risk",s:14,max:100,l:"Low",g:true},
            {icon:"🩸",t:"Anemia Risk",s:18,max:100,l:"Low",g:true},
          ].map((a,i) => (
            <div key={i} className="wh-assess-card">
              <p className="wh-assess-title">{a.icon} {a.t}</p>
              <p className="wh-assess-score" style={a.o?{color:"var(--warn500)"}:a.g?{color:"var(--ok500)"}:{}}>{a.s}</p>
              <p className="wh-assess-sub">Score {a.s} /{a.max}</p>
              <span className={a.o?"badge-orange":a.g?"badge-green":"badge-purple"}>{a.l}</span>
              <div className="risk-bar" style={{marginTop:5}}><div className={a.o?"risk-fill-orange":a.g?"risk-fill-green":"risk-fill-orange"} style={{width:`${a.s}%`}} /></div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Actions */}
      <div className="card">
        <div className="sec-head">
          <h2 className="sec-title">AI-Generated Next Best Actions</h2>
          <span style={{fontSize:10,color:"var(--cc)"}}>Powered by Avexa AI ✨</span>
        </div>
        <div className="wh-ai-grid">
          {[
            {icon:"📋",t:"Complete fertility readiness assessment",d:"Evaluate key biomarkers and optimize factors for conception.",btn:"Start Assessment >"},
            {icon:"📅",t:"Book gynecology consultation",d:"Discuss cycle, PCOS management and preconception plan.",btn:"Book Appointment >"},
            {icon:"🧪",t:"Order TSH and HbA1c",d:"Early detection helps improve fertility outcomes.",btn:"Order Tests >"},
            {icon:"🥗",t:"Start nutrition plan",d:"Personalized plan to support fertility & hormonal balance.",btn:"View Plan >"},
            {icon:"📱",t:"Track ovulation & symptoms",d:"Monitor cycles and symptoms for better insights.",btn:"Open Tracker >"},
          ].map((a,i) => (
            <div key={i} className="wh-ai-card">
              <div className="wh-ai-icon">{a.icon}</div>
              <p className="wh-ai-title">{a.t}</p>
              <p className="wh-ai-desc">{a.d}</p>
              <button className="wh-ai-btn">{a.btn}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Care Plan */}
      <div className="card">
        <div className="sec-head"><h2 className="sec-title">Care Plan & Milestones</h2></div>
        <CarePlanSection
          goals={["Conceive within 6 months","Regulate cycle and ovulation","Optimize nutrition & weight","Manage PCOS symptoms"]}
          programs={[{name:"Preconception Care Program",status:"In Progress"},{name:"PCOS Management Program",status:"In Progress"},{name:"Nutrition & Lifestyle Coaching",status:"In Progress"}]}
          tasks={[{name:"Complete TSH Test",due:"Due on 23 May 2026"},{name:"Ovulation Tracking Review",due:"Due on 23 May 2026"},{name:"Follow-up Consultation",due:"Due on 01 Jun 2026"}]}
          followup={{date:"01 Jun 2026, 10:30 AM",doctor:"Dr. Ananya Sharma",specialty:"Gynecologist",clinic:"Avexa Women's Health Clinic, Mumbai"}}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   NAVIGATION SHELL
───────────────────────────────────────────────────── */
const NAV = [
  {id:"overview",label:"Overview",icon:"⊞"},
  {id:"lifestage",label:"Life Stage",icon:"🌸"},
  {id:"clinical",label:"Clinical History",icon:"📋"},
  {id:"womenshealth",label:"Women's Health",icon:"💜"},
  {id:"assessments",label:"Assessments",icon:"📊"},
  {id:"labs",label:"Labs & Reports",icon:"🧪"},
  {id:"timeline",label:"Timeline",icon:"⏱"},
  {id:"careplan",label:"Care Plan",icon:"🎯"},
  {id:"programs",label:"Programs",icon:"📱"},
  {id:"appointments",label:"Appointments",icon:"📅"},
  {id:"messages",label:"Messages",icon:"💬",badge:3},
  {id:"documents",label:"Documents",icon:"📄"},
];
const SCREENS_MAP = {overview:OverviewScreen,lifestage:LifeStageScreen,clinical:ClinicalHistoryScreen,womenshealth:WomensHealthScreen};

const shellCSS = `
.whr-shell{display:flex;height:100vh;overflow:hidden;font-family:var(--ff);background:var(--bg)}
.whr-sidebar{width:158px;min-width:158px;background:var(--dark);display:flex;flex-direction:column;overflow-y:auto;flex-shrink:0}
.whr-logo{padding:16px 14px 13px;border-bottom:1px solid rgba(255,255,255,0.08)}
.whr-logo-mark{width:27px;height:27px;border-radius:7px;background:var(--s500);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#fff;flex-shrink:0}
.whr-logo-name{color:#fff;font-weight:700;font-size:14px;letter-spacing:-.3px}
.whr-logo-sub{color:var(--s400);font-size:9px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;margin-top:1px}
.whr-nav{flex:1;padding:8px 0}
.whr-nav-btn{display:flex;align-items:center;gap:8px;padding:8px 12px;margin:1px 7px;border-radius:var(--r-sm);cursor:pointer;background:transparent;color:rgba(255,255,255,0.58);font-size:12px;font-weight:400;transition:var(--tr-f);border:none;width:calc(100% - 14px);text-align:left;font-family:var(--ff);position:relative}
.whr-nav-btn:hover{background:rgba(255,255,255,0.07);color:rgba(255,255,255,0.9)}
.whr-nav-btn.active{background:var(--p700);color:#fff;font-weight:600}
.whr-nav-icon{font-size:13px;width:16px;text-align:center;flex-shrink:0}
.whr-nav-badge{position:absolute;right:9px;background:var(--s500);color:#fff;border-radius:99px;font-size:9px;font-weight:700;padding:0 5px;line-height:14px;min-width:14px;text-align:center}
.whr-ai-panel{margin:8px;border-radius:var(--r-md);background:linear-gradient(135deg,var(--p600),var(--p800));padding:10px 11px;border:1px solid rgba(255,255,255,0.1)}
.whr-ai-lbl{color:#fff;font-weight:700;font-size:11px;margin-bottom:2px;display:flex;align-items:center;gap:4px}
.whr-ai-sub{color:rgba(255,255,255,0.62);font-size:10px;line-height:1.4}
.whr-settings{padding:10px 13px;border-top:1px solid rgba(255,255,255,0.08);display:flex;align-items:center;gap:7px;color:rgba(255,255,255,0.45);font-size:12px;cursor:pointer}
.whr-main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0}
.whr-topbar{height:50px;background:#fff;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 18px;flex-shrink:0;box-shadow:var(--sh-xs)}
.whr-search{display:flex;align-items:center;gap:8px;background:var(--bg);border:1px solid var(--border);border-radius:var(--r-md);padding:6px 13px;font-size:12px;color:var(--cc);width:260px;cursor:text}
.whr-search-kbd{margin-left:auto;font-size:10px;color:var(--cc);background:var(--border);padding:1px 5px;border-radius:3px}
.whr-topright{display:flex;align-items:center;gap:12px}
.whr-icon-btn{background:none;border:none;cursor:pointer;font-size:17px;color:var(--cc);position:relative;display:flex;align-items:center}
.whr-notif-badge{position:absolute;top:-3px;right:-4px;background:var(--err500);color:#fff;border-radius:99px;font-size:8px;font-weight:700;padding:0 4px;line-height:13px;min-width:13px;text-align:center}
.whr-profile-chip{display:flex;align-items:center;gap:7px;cursor:pointer;padding:4px 7px;border-radius:var(--r-md);border:1px solid var(--border);background:var(--g50)}
.whr-avatar{width:28px;height:28px;border-radius:50%;background:var(--p100);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:var(--p700);flex-shrink:0}
.whr-content{flex:1;overflow-y:auto}
.whr-mobile-toggle{display:none;background:none;border:none;font-size:20px;cursor:pointer;color:var(--ch)}
.whr-mobile-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:199}
@media(max-width:768px){
  .whr-sidebar{position:fixed!important;top:0;bottom:0;z-index:200;transition:left 0.25s ease}
  .whr-sidebar.closed{left:-160px}
  .whr-sidebar.open{left:0}
  .whr-mobile-toggle{display:block}
  .whr-mobile-overlay.open{display:block}
  .whr-search{width:160px}
}
`;

export default function WomenHealthRecord() {
  const [active, setActive] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const ActiveScreen = SCREENS_MAP[active] || OverviewScreen;

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <style>{overviewCSS}</style>
      <style>{lifeStageCSS}</style>
      <style>{clinicalCSS}</style>
      <style>{womensHealthCSS}</style>
      <style>{shellCSS}</style>

      <div className="whr-shell">
        {/* Mobile overlay */}
        <div className={`whr-mobile-overlay${mobileOpen?" open":""}`} onClick={() => setMobileOpen(false)} />

        {/* Sidebar */}
        <aside className={`whr-sidebar${mobileOpen?" open":" closed"}`}>
          <div className="whr-logo">
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div className="whr-logo-mark">a</div>
              <div>
                <div className="whr-logo-name">avexa</div>
                <div className="whr-logo-sub">onecare</div>
              </div>
            </div>
          </div>

          <nav className="whr-nav">
            {NAV.map(item => (
              <button key={item.id} className={`whr-nav-btn${active===item.id?" active":""}`}
                onClick={() => { setActive(item.id); setMobileOpen(false); }}
              >
                <span className="whr-nav-icon">{item.icon}</span>
                <span style={{flex:1}}>{item.label}</span>
                {item.badge && <span className="whr-nav-badge">{item.badge}</span>}
              </button>
            ))}
          </nav>

          <div className="whr-ai-panel">
            <div className="whr-ai-lbl">✨ Avexa AI</div>
            <div style={{color:"rgba(255,255,255,0.85)",fontSize:10,fontWeight:600,margin:"2px 0 3px"}}>Clinical Copilot</div>
            <p className="whr-ai-sub">Ask anything about this patient</p>
          </div>

          <div className="whr-settings">
            <span>⚙️</span><span>Settings</span><span style={{marginLeft:"auto",fontSize:10}}>›</span>
          </div>
        </aside>

        {/* Main */}
        <div className="whr-main">
          {/* Topbar */}
          <header className="whr-topbar">
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button className="whr-mobile-toggle" onClick={() => setMobileOpen(true)}>☰</button>
              <div className="whr-search">
                <span>🔍</span>
                <span>Search patient, records…</span>
                <span className="whr-search-kbd">Ctrl+K</span>
              </div>
            </div>
            <div className="whr-topright">
              <button className="whr-icon-btn">
                🔔<span className="whr-notif-badge">3</span>
              </button>
              <button className="whr-icon-btn">❓</button>
              <div className="whr-profile-chip">
                <div className="whr-avatar">A</div>
                <div>
                  <div style={{fontSize:12,fontWeight:600,color:"var(--ch)"}}>Dr. Ananya Sharma</div>
                  <div style={{fontSize:10,color:"var(--cc)"}}>Gynecologist</div>
                </div>
                <span style={{fontSize:10,color:"var(--cc)",marginLeft:2}}>▾</span>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="whr-content">
            {SCREENS_MAP[active] ? (
              <ActiveScreen />
            ) : (
              <div style={{padding:"48px 28px",textAlign:"center"}}>
                <div style={{fontSize:40,marginBottom:10}}>🚧</div>
                <h2 style={{fontSize:17,color:"var(--ch)",marginBottom:5}}>Coming Soon</h2>
                <p style={{fontSize:12,color:"var(--cc)"}}>This section is under development.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}